import axiosClient from "../axiosInstance";
import {
  ProductionPark,
  ProductionParkData,
  ProductionParkDataObject,
  ProductionParkRequest,
} from "../models/ProductionPark";
import { parseAxiosError } from "./helpers/util";
import {
  ProductionParkComponent,
  ProductionParkComponentData,
} from "../models/ProductionParkComponent";

export const getAllProductionParks = async (
  page: number,
  size: number,
  filters: { [key: string]: string } = {},
): Promise<ProductionParkData> => {
  const client = axiosClient();

  const queryParams = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    ...filters,
  }).toString();

  const response = await client.get(`/production-parks?${queryParams}`);

  return response.data;
};

export const getAllProductionParksByClientId =
  async (): Promise<ProductionParkData> => {
    const client = axiosClient();

    const response = await client.get(`/production-parks/client`);

    return response.data;
  };

export const getProductionParkById = async (
  productionParkId: ProductionPark | number,
): Promise<ProductionParkData> => {
  const client = axiosClient();

  const response = await client.get(`/production-parks/${productionParkId}`);

  return response.data;
};

export const createProductionPark = async (
  data: ProductionParkRequest,
): Promise<ProductionParkDataObject> => {
  const client = axiosClient();

  const response = await client.post(`/production-parks`, data);
  return response.data;
};

export const updateProductionPark = async (
  productionParkId: number,
  data: Partial<ProductionParkRequest>,
): Promise<ProductionParkData> => {
  const client = axiosClient();

  const response = await client.put(
    `/production-parks/${productionParkId}`,
    data,
  );
  return response.data;
};

export const deleteProductionPark = async (productionParkId: number) => {
  const client = axiosClient();

  const response = await client.delete(`/production-parks/${productionParkId}`);

  return response.data;
};

export const updateProductionParkDetails = async (
  componentId: number,
  data: Partial<ProductionParkComponent>,
): Promise<ProductionParkComponentData> => {
  const client = axiosClient();

  const response = await client.put(
    `/production-park-components/${componentId}`,
    data,
  );
  return response.data;
};

export const removeProductionParkComponentMaintenance = async (
  componentId: number,
  serviceId: number,
): Promise<ProductionParkData> => {
  const client = axiosClient();

  try {
    const response = await client.patch(
      `/production-park-components/${componentId}/clear-maintenance/${serviceId}`,
    );
    return response.data;
  } catch (error) {
    const message = parseAxiosError(error);
    throw Error(message);
  }
};

export const removeProductionParkComponentLimit = async (
  componentId: number,
  limitId: number,
): Promise<ProductionParkData> => {
  const client = axiosClient();

  const response = await client.patch(
    `/production-park-components/${componentId}/clear-limit/${limitId}`,
  );
  return response.data;
};

export const saveProductionParkDefaultWeights = async (
  parkId: number,
  data: any,
): Promise<any> => {
  const client = axiosClient();

  const response = await client.post(
    `/production-parks/${parkId}/weights`,
    data,
  );
  return response.data;
};

export const saveProductionParkLimit = async (postData: any): Promise<any> => {
  const client = axiosClient();

  const response = await client.post(`/production-park-limitations`, postData);

  return response.data;
};

export const updateProductionParkLimit = async (
  limitId: number,
  postData: any,
): Promise<any> => {
  const client = axiosClient();

  const response = await client.put(
    `/production-park-limitations/${limitId}`,
    postData,
  );

  return response.data;
};

export const deleteProductionParkLimit = async (
  productionParkLimitationId: number,
): Promise<any> => {
  const client = axiosClient();

  const response = await client.delete(
    `/production-park-limitations/${productionParkLimitationId}`,
  );

  return response.data;
};

export const getProductionParkLimitations = async (
  production_park_id: number,
  fromDateTime?: string,
  toDateTime?: string,
): Promise<any> => {
  const client = axiosClient();

  const response = await client.get(
    `/production-parks/${production_park_id}/limitations`,
    {
      params: {
        ...(fromDateTime && {
          fromDateTime: fromDateTime,
        }),
        ...(toDateTime && {
          toDateTime: toDateTime,
        }),
      },
    },
  );

  return response.data;
};

export const getSupplyPlanForecast = async (
  productionParkId: number,
  auctionTypeId: number,
  date: string,
): Promise<any> => {
  const client = axiosClient();

  const response = await client.get(
    `/production-parks/${productionParkId}/supply-plans/forecast?auctionTypeId=${auctionTypeId}&date=${date}`,
  );

  return response.data;
};

export const saveProductionParkSupplyPlanForecast = async (
  productionParkId: number,
  data: any,
): Promise<any> => {
  const client = axiosClient();

  const response = await client.post(
    `/production-parks/${productionParkId}/supply-plans/forecast`,
    data,
  );

  return response.data;
};

export const uploadProductionParkSupplyPlanForecast = async (
  productionParkId: number,
  auctionTypeId: number,
  data: any,
): Promise<any> => {
  const client = axiosClient();

  const response = await client.post(
    `/production-parks/${productionParkId}/supply-plans/forecast/import`,
    { auctionTypeId: auctionTypeId, file: data },
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
};

export const fetchExportProductionParks = async (): Promise<void> => {
  const client = axiosClient();

  const response = await client.get(`/production-parks/export`, {
    responseType: "blob",
  });

  return response.data;
};

export const getBatteriesByProductionParkId = async (
  productionParkId: number,
): Promise<any> => {
  const client = axiosClient();

  const response = await client.get(`/batteries`, {
    params: {
      productionParkId,
    },
  });

  return response.data;
};

export const createBattery = async (data: any): Promise<any> => {
  const client = axiosClient();

  const response = await client.post(`/batteries`, data);
  return response.data;
};

export const updateBattery = async (
  batteryId: number,
  data: any,
): Promise<any> => {
  const client = axiosClient();

  const response = await client.put(`/batteries/${batteryId}`, data);
  return response.data;
};

export const deleteBattery = async (batteryId: number): Promise<any> => {
  const client = axiosClient();

  const response = await client.delete(`/batteries/${batteryId}`);
  return response.data;
};

export const linkConsumptionForecastTemplate = async (
  productionParkId: number,
  prognosisId: number | null,
): Promise<any> => {
  const client = axiosClient();

  const response = await client.post(
    `/production-parks/${productionParkId}/link-consumption-forecast-template`,
    { prognosisId },
  );
  return response.data;
};
